import { useQuery } from "@apollo/client";
import { Tags, getUserTagValueOffEvent } from "@clockwise/client-commons/src/util/event-tag";
import {
  EventColorCategory,
  IEventWithAnnotatedEvents,
  useCalendarColors,
} from "@clockwise/web-commons/src/util/event-category-coloring";
import { getPreviousWeekday } from "@clockwise/web-commons/src/util/getPreviousWeekday";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { intersection, isEmpty, isEqual, keys } from "lodash";
import { uniq } from "lodash/fp";
import { DateTime, DurationLike } from "luxon";
import React, { useCallback, useEffect } from "react";
import { useOrgId } from "../../hooks/useOrgId";
import { useUserProfile } from "../../hooks/useUserProfile";
import {
  PlannerEventsQueryDocument,
  PlannerEventsQueryQuery,
} from "../apollo/__generated__/Planner.generated";
import {
  PlannerEvent,
  PlannerEventCard,
  PlannerEventCardsByDay,
  UseCalendarEventCardsHook,
} from "../types";
import { parseCalendarDataGQL } from "../util/parseCalendarDataGQL";
import usePlannerMetaData from "./usePlannerMetaData";

export const usePlannerEventCards: UseCalendarEventCardsHook = (
  date: string,
  calendarIds: string[],
) => {
  // @deprecated: maintaining this for backwards compatibility
  const [events, setEvents] = React.useState<PlannerEvent[]>([]);

  const [cardsByDay, setCardsByDay] = React.useState<PlannerEventCardsByDay>({});
  const [weekStartDate, setWeekStartDate] = React.useState<string>("");
  const { getEventColors } = useCalendarColors();
  const { userProfile } = useUserProfile();
  const { colorSettings } = usePlannerMetaData();

  const eventColorGetter = useCallback(
    (event: IEventWithAnnotatedEvents) => {
      return getEventColors(event, colorSettings, userProfile.userId || "", false, true);
    },
    [colorSettings, getEventColors, userProfile.userId],
  );

  const eventCategoryGetter = useCallback(
    (event: IEventWithAnnotatedEvents) => {
      return getUserTagValueOffEvent(event, Tags.EventColoringCategory, userProfile.userId || "")
        ?.value as EventColorCategory;
    },
    [userProfile.userId],
  );

  const { orgId } = useOrgId();

  const timeZone = getRenderTimeZone();

  const newWeekStartDate = getPreviousWeekday(DateTime.fromISO(date), "sunday").toISODate();
  if (!isEqual(newWeekStartDate, weekStartDate)) {
    setWeekStartDate(newWeekStartDate);
  }

  const parseAndAddEvents = useCallback(
    (data: PlannerEventsQueryQuery) => {
      const [newCardsByDay, newEvents] = parseCalendarDataGQL(
        data,
        [weekStartDate],
        eventColorGetter,
        eventCategoryGetter,
      );
      setEvents((currEvents) => uniq([...currEvents, ...newEvents]));
      setCardsByDay((currEventsByDay) => ({ ...currEventsByDay, ...newCardsByDay }));
    },
    [eventCategoryGetter, eventColorGetter, weekStartDate],
  );

  const { loading, error, startPolling, stopPolling, refetch, fetchMore, called } = useQuery(
    PlannerEventsQueryDocument,
    {
      skip: !orgId || !weekStartDate,
      variables: {
        orgId: orgId as string,
        weekStartDates: [weekStartDate],
        timeZone,
        calendarIds,
        includeAllDay: true,
      },
      context: { useBatching: false },
      onCompleted: parseAndAddEvents,
      /*
      NB: Known Apollo bug where refetches don't trigger onCompleted callbacks
      Setting notifyOnNetworkStatusChange to true is a workaround to force component re-render
      Could not use useEffect since returned data is cached version due to missing IDs on EventParent
      */
      notifyOnNetworkStatusChange: true,
      errorPolicy: "all", // This allows us to receive both error and data to able to load partial data.
    },
  );

  useEffect(() => {
    // There's a bug where if the variables change, the useQuery doesn't retrigger
    // The most important case we want to cover right now is the `calendarIds` changes
    // This can happen after proposal is accepted and then all the `calendarIds` are updated
    // At that point we need to update the events via `refetch`
    if (!called) {
      // We only want this logic to run after the first time the query is called
      return;
    }
    void refetch().then((result) => parseAndAddEvents(result.data));
  }, [JSON.stringify(calendarIds)]);

  const refetchAndstartPolling = useCallback((time: number) => {
    void refetch();
    startPolling(time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prefetch = useCallback(
    (relativeDuration: DurationLike) => {
      if (!orgId) {
        return;
      }

      const weekStartDates = [
        getPreviousWeekday(
          DateTime.fromISO(weekStartDate).plus(relativeDuration),
          "sunday",
        ).toISODate(),
      ];

      void fetchMore({
        variables: {
          orgId,
          weekStartDates,
        },
      }).then(void parseAndAddEvents);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekStartDate],
  );

  // @deprecated: maintaining this for backwards compatibility
  const getEventForCard = useCallback(
    (card: PlannerEventCard) => {
      return events.find((event) => event.eventKey.externalEventId === card.externalEventId);
    },
    [events],
  );

  // P: When we remove calendarIds, the data updates really slowly
  // requiring multiple call cycles of this hook before we get all the correct data removals
  // (i.e. the data still contains cards for some of the removed calendarIds)
  // This results in a lot of flickering in the UI and causes UI confusion!
  // S: We can just skip the call cycles, just filter the data we already have
  // and this prevents the flickering of events
  const cardsByDayFilteredByCalendarIds: PlannerEventCardsByDay = {};
  keys(cardsByDay).forEach((date) => {
    const cards = cardsByDay[date];
    cardsByDayFilteredByCalendarIds[date] = cards.filter(
      (card) => !isEmpty(intersection(calendarIds, card.calendarIds)),
    );
  });

  return {
    cardsByDay: cardsByDayFilteredByCalendarIds,
    loading,
    error,
    getEventForCard,
    refetch: () => void refetch(),
    prefetch,
    startPolling: refetchAndstartPolling,
    stopPolling,
  };
};
